import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Alert, Stack, InputAdornment, 
  Breadcrumbs, Link, Chip, Tooltip, Avatar, Checkbox, Menu, MenuItem, Drawer, Divider
} from '@mui/material';
import { 
  Add as AddIcon, Edit as EditIcon,
  Search as SearchIcon, LibraryBooks, FilterList,
  Download as DownloadIcon, Person as PersonIcon,
  Timeline as TimelineIcon, Star as StarIcon, InfoOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';

const SubjectsPage = () => {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // UI States
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkAnchorEl, setBulkAnchorEl] = useState<null | HTMLElement>(null);
  
  // Drawer State
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<any>(null);

  const [formData, setFormData] = useState({
    id: '',
    name: '',
    code: '',
    description: '',
    credits: 1,
    grade: '',
    status: 'ACTIVE'
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const res = await axios.get(getApiUrl('subjects'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(res.data);
      setSelectedIds([]);
    } catch (err: any) {
      console.error('Error loading subjects:', err);
      setError(err.response?.data?.message || 'Failed to load subjects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      if (formData.id) {
        await axios.put(getApiUrl(`subjects/${formData.id}`), formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(getApiUrl('subjects'), formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setOpenDialog(false);
      setFormData({ id: '', name: '', code: '', description: '', credits: 1, grade: '', status: 'ACTIVE' });
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save subject');
    }
  };

  const handleBulkAction = async (action: 'DELETE' | 'ARCHIVE' | 'ACTIVATE') => {
    if (!selectedIds.length) return;
    
    try {
      setLoading(true);
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      
      if (action === 'DELETE') {
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} subjects?`)) return;
        await axios.post(getApiUrl('subjects/bulk-delete'), { ids: selectedIds }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const status = action === 'ARCHIVE' ? 'ARCHIVED' : 'ACTIVE';
        await axios.post(getApiUrl('subjects/bulk-status'), { ids: selectedIds, status }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Bulk action failed');
    } finally {
      setBulkAnchorEl(null);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) setSelectedIds(filteredSubjects.map(s => s.id));
    else setSelectedIds([]);
  };

  const handleSelectOne = (id: string) => {
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(i => i !== id));
    else setSelectedIds([...selectedIds, id]);
  };

  const openEditDialog = (subject: any) => {
    setFormData({
      id: subject.id, name: subject.name, code: subject.code, 
      description: subject.description || '', credits: subject.credits || 1, 
      grade: subject.grade, status: subject.status || 'ACTIVE'
    });
    setOpenDialog(true);
  };

  const openDrawer = (subject: any) => {
    setSelectedSubject(subject);
    setDrawerOpen(true);
  };

  const filteredSubjects = subjects.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.grade.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Chart Data preparation
  const gradeDistribution = subjects.reduce((acc, curr) => {
    acc[curr.grade] = (acc[curr.grade] || 0) + 1;
    return acc;
  }, {});
  
  const chartData = Object.keys(gradeDistribution).map(key => ({
    name: key, count: gradeDistribution[key]
  })).sort((a,b) => b.count - a.count).slice(0, 5); // top 5
  
  const COLORS = ['#4C51BF', '#3182CE', '#38B2AC', '#48BB78', '#ECC94B'];

  return (
    <Box sx={{ p: 4, bgcolor: '#F7FAFC', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 1, fontSize: '0.85rem' }}>
          <Link underline="hover" color="inherit" onClick={() => navigate('/dashboard')} sx={{ cursor: 'pointer' }}>Dashboard</Link>
          <Link underline="hover" color="inherit" onClick={() => navigate('/dashboard/academics')} sx={{ cursor: 'pointer' }}>Academics</Link>
          <Typography color="text.primary">Curriculum Manager</Typography>
        </Breadcrumbs>
        <Stack direction="row" justifyContent="space-between" alignItems="center flex-wrap gap-2">
          <Box>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#263B7C', letterSpacing: '-0.02em', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
              Curriculum Manager
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Centralized hub for tracking courses, credits, and syllabus data.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button 
                variant="outlined" 
                startIcon={<DownloadIcon />}
                sx={{ borderRadius: '12px', px: 3, fontWeight: 700, borderColor: '#E2E8F0', color: '#4A5568', bgcolor: 'white' }}
            >
                Export CSV
            </Button>
            <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => {
                  setFormData({ id: '', name: '', code: '', description: '', credits: 1, grade: '', status: 'ACTIVE' });
                  setOpenDialog(true);
                }}
                sx={{ 
                    bgcolor: '#263B7C', borderRadius: '12px', px: 3,
                    boxShadow: '0 4px 14px rgba(38,59,124,0.25)',
                    '&:hover': { bgcolor: '#1e2e60' }
                }}
            >
                New Subject
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Analytics Ribbon */}
      {!loading && subjects.length > 0 && (
         <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', lg: '1fr 2fr' }, 
            gap: 4, mb: 4 
         }}>
             {/* Key Metrics */}
             <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                 <Paper sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #edf2f7' }}>
                     <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Total Subjects</Typography>
                     <Typography variant="h3" fontWeight={800} color="#2D3748" sx={{ mt: 1, mb: 2 }}>{subjects.length}</Typography>
                     <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={`${subjects.filter(s => s.status === 'ACTIVE').length} Active`} size="small" sx={{ bgcolor: '#C6F6D5', color: '#22543D', fontWeight: 700, borderRadius: '6px' }} />
                        <Chip label={`${subjects.filter(s => s.status === 'ARCHIVED').length} Archived`} size="small" sx={{ bgcolor: '#E2E8F0', color: '#4A5568', fontWeight: 700, borderRadius: '6px' }} />
                     </Stack>
                 </Paper>
                 <Paper sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #edf2f7' }}>
                     <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Total Credits</Typography>
                     <Typography variant="h3" fontWeight={800} color="#2D3748" sx={{ mt: 1, mb: 2 }}>
                         {subjects.reduce((sum, s) => sum + (s.credits || 0), 0)}
                     </Typography>
                     <Typography variant="body2" color="text.secondary" fontWeight={600}>
                         Across {Object.keys(gradeDistribution).length} Grades
                     </Typography>
                 </Paper>
             </Box>
             
             {/* Chart */}
             <Paper sx={{ p: 3, borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #edf2f7', display: 'flex', flexDirection: 'column' }}>
                 <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase" sx={{ mb: 1 }}>Subject Distribution by Grade</Typography>
                 <Box sx={{ flex: 1, minHeight: 120 }}>
                     <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#718096', fontSize: 12, fontWeight: 600 }} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fill: '#718096', fontSize: 12, fontWeight: 600 }} />
                          <RechartsTooltip cursor={{ fill: '#F7FAFC' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                          <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={40}>
                            {chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                     </ResponsiveContainer>
                 </Box>
             </Paper>
         </Box>
      )}

      {/* Main Table Area */}
      <Paper sx={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #edf2f7', overflow: 'hidden' }}>
          
        {/* Bulk Action Toolbar */}
        <Box sx={{ p: 2.5, borderBottom: '1px solid #edf2f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: selectedIds.length > 0 ? '#EBF4FF' : 'white', transition: 'background-color 0.3s' }}>
            <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  placeholder="Search by name, code, or grade..."
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ width: 320, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button startIcon={<FilterList />} sx={{ color: '#4A5568', fontWeight: 600, borderRadius: '10px' }}>Advanced Filters</Button>
            </Stack>

            {selectedIds.length > 0 && (
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body2" fontWeight={800} color="#2B6CB0">
                        {selectedIds.length} subjects selected
                    </Typography>
                    <Button 
                        variant="contained" 
                        size="small"
                        onClick={(e) => setBulkAnchorEl(e.currentTarget)}
                        sx={{ bgcolor: '#2B6CB0', borderRadius: '8px', '&:hover': { bgcolor: '#2C5282' } }}
                    >
                        Bulk Actions
                    </Button>
                    <Menu
                        anchorEl={bulkAnchorEl}
                        open={Boolean(bulkAnchorEl)}
                        onClose={() => setBulkAnchorEl(null)}
                        PaperProps={{ elevation: 3, sx: { borderRadius: '12px', minWidth: 160 } }}
                    >
                        <MenuItem onClick={() => handleBulkAction('ACTIVATE')} sx={{ fontWeight: 600, color: '#38A169', fontSize: '0.85rem' }}>Set ACTIVE</MenuItem>
                        <MenuItem onClick={() => handleBulkAction('ARCHIVE')} sx={{ fontWeight: 600, color: '#718096', fontSize: '0.85rem' }}>Set ARCHIVED</MenuItem>
                        <Divider />
                        <MenuItem onClick={() => handleBulkAction('DELETE')} sx={{ fontWeight: 600, color: '#E53E3E', fontSize: '0.85rem' }}>Delete Selected</MenuItem>
                    </Menu>
                </Stack>
            )}
        </Box>

        {error && <Alert severity="error" sx={{ m: 2, borderRadius: '12px' }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#F8F9FA' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                     <Checkbox 
                        checked={filteredSubjects.length > 0 && selectedIds.length === filteredSubjects.length}
                        indeterminate={selectedIds.length > 0 && selectedIds.length < filteredSubjects.length}
                        onChange={handleSelectAll}
                     />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Subject Info</TableCell>
                  <TableCell sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Grade/Level</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Credits</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: '#4A5568', textTransform: 'uppercase', fontSize: '0.75rem' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSubjects.map((subject) => (
                  <TableRow 
                    key={subject.id} 
                    hover 
                    selected={selectedIds.includes(subject.id)}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer' }}
                    onClick={() => openDrawer(subject)}
                  >
                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                        <Checkbox 
                            checked={selectedIds.includes(subject.id)}
                            onChange={() => handleSelectOne(subject.id)}
                        />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ bgcolor: `${subject.status === 'ACTIVE' ? '#4C51BF' : '#A0AEC0'}15`, color: subject.status === 'ACTIVE' ? '#4C51BF' : '#A0AEC0', width: 40, height: 40, borderRadius: '10px' }}>
                          <LibraryBooks sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={800} color="#2D3748">{subject.name}</Typography>
                          <Chip label={subject.code} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: '#EDF2F7', color: '#4A5568', mt: 0.5, borderRadius: '4px' }} />
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} color="text.secondary">{subject.grade}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={800} color="#263B7C">{subject.credits || 1}</Typography>
                    </TableCell>
                    <TableCell align="center">
                       {subject.status === 'ACTIVE' ? (
                          <Chip label="ACTIVE" size="small" sx={{ fontWeight: 800, color: '#38A169', bgcolor: '#C6F6D5', borderRadius: '8px', fontSize: '0.65rem' }} />
                       ) : (
                          <Chip label="ARCHIVED" size="small" sx={{ fontWeight: 800, color: '#718096', bgcolor: '#E2E8F0', borderRadius: '8px', fontSize: '0.65rem' }} />
                       )}
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <Tooltip title="View Details">
                        <IconButton size="small" sx={{ color: '#4A5568' }} onClick={() => openDrawer(subject)}>
                            <InfoOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Subject">
                        <IconButton size="small" sx={{ color: '#4C51BF' }} onClick={() => openEditDialog(subject)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredSubjects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                      <Box sx={{ opacity: 0.5 }}>
                        <LibraryBooks sx={{ fontSize: 48, mb: 1 }} />
                        <Typography variant="body1" fontWeight={600}>No subjects found</Typography>
                        <Typography variant="body2">Try adjusting your search filters or add a new subject.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Subject Drawer (Detailed View) */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, p: 0, bgcolor: '#F7FAFC' } }}
      >
        {selectedSubject && (
            <Box>
                {/* Drawer Header */}
                <Box sx={{ p: 4, bgcolor: 'white', borderBottom: '1px solid #edf2f7' }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start" mb={2}>
                        <Avatar sx={{ bgcolor: '#4C51BF15', color: '#4C51BF', width: 56, height: 56, borderRadius: '16px' }}>
                           <LibraryBooks sx={{ fontSize: 28 }} />
                        </Avatar>
                        <Box flex={1}>
                            <Typography variant="h5" fontWeight={800} color="#2D3748" sx={{ lineHeight: 1.2 }}>{selectedSubject.name}</Typography>
                            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mt: 0.5 }}>{selectedSubject.code} • {selectedSubject.grade}</Typography>
                        </Box>
                        {selectedSubject.status === 'ACTIVE' ? (
                          <Chip label="ACTIVE" size="small" sx={{ fontWeight: 800, color: '#38A169', bgcolor: '#C6F6D5', borderRadius: '8px' }} />
                       ) : (
                          <Chip label="ARCHIVED" size="small" sx={{ fontWeight: 800, color: '#718096', bgcolor: '#E2E8F0', borderRadius: '8px' }} />
                       )}
                    </Stack>
                    <Button 
                        fullWidth variant="outlined" 
                        startIcon={<EditIcon />} 
                        onClick={() => { setDrawerOpen(false); openEditDialog(selectedSubject); }}
                        sx={{ borderRadius: '10px', fontWeight: 700, borderColor: '#E2E8F0', color: '#4A5568' }}
                    >
                        Edit Subject Data
                    </Button>
                </Box>

                {/* Drawer Body */}
                <Box sx={{ p: 4 }}>
                    <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase">Curriculum Details</Typography>
                    
                    <Paper sx={{ p: 3, borderRadius: '16px', mt: 2, border: '1px solid #edf2f7', boxShadow: 'none' }}>
                        <Stack spacing={3}>
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                    <StarIcon sx={{ fontSize: 16, color: '#D69E2E' }} />
                                    <Typography variant="body2" fontWeight={800} color="#2D3748">Credit Value</Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary" pl={3}>{selectedSubject.credits || 1} Academic Credits</Typography>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="body2" fontWeight={800} color="#2D3748" mb={1}>Official Description</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                                    {selectedSubject.description || "No description provided for this subject. Update the subject definition to add syllabus goals and curriculum details."}
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>

                    <Typography variant="caption" fontWeight={800} color="text.secondary" textTransform="uppercase" sx={{ display: 'block', mt: 4 }}>Enrollment Overview</Typography>
                    <Paper sx={{ p: 3, borderRadius: '16px', mt: 2, border: '1px solid #edf2f7', boxShadow: 'none' }}>
                         <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                             <Avatar sx={{ bgcolor: '#EBF4FF', color: '#3182CE' }}><PersonIcon /></Avatar>
                             <Box>
                                 <Typography variant="h6" fontWeight={800}>~ 45</Typography>
                                 <Typography variant="caption" color="text.secondary" fontWeight={600}>Estimated Students Enrolled</Typography>
                             </Box>
                         </Stack>
                         <Stack direction="row" alignItems="center" spacing={2}>
                             <Avatar sx={{ bgcolor: '#EBF4FF', color: '#3182CE' }}><TimelineIcon /></Avatar>
                             <Box>
                                 <Typography variant="h6" fontWeight={800}>3</Typography>
                                 <Typography variant="caption" color="text.secondary" fontWeight={600}>Active Class Groups</Typography>
                             </Box>
                         </Stack>
                    </Paper>
                </Box>
            </Box>
        )}
      </Drawer>

      {/* Add / Edit Subject Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#263B7C', pb: 1 }}>
            {formData.id ? 'Edit Subject Definition' : 'Add New Subject'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
              <Box sx={{ gridColumn: 'span 2' }}>
                <TextField 
                  fullWidth label="Subject Name" required placeholder="e.g. Advanced Mathematics"
                  value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                />
              </Box>
              <Box>
                <TextField 
                  fullWidth label="Subject Code" required placeholder="e.g. MAT-401"
                  value={formData.code} onChange={e => setFormData({ ...formData, code: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                />
              </Box>
              <Box>
                <TextField 
                  fullWidth label="Credits" type="number" required InputProps={{ inputProps: { min: 0, max: 20 } }}
                  value={formData.credits} onChange={e => setFormData({ ...formData, credits: Number(e.target.value) })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                />
              </Box>
              <Box>
                <TextField 
                  fullWidth label="Target Grade/Level" required placeholder="e.g. Grade 10"
                  value={formData.grade} onChange={e => setFormData({ ...formData, grade: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                />
              </Box>
              <Box>
                <TextField 
                  select fullWidth label="Status" required
                  value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                >
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="ARCHIVED">Archived</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ gridColumn: 'span 2' }}>
                <TextField 
                  fullWidth label="Official Syllabus Description" multiline rows={4}
                  placeholder="Outline the goals, prerequisites, and general curriculum..."
                  value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} 
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={() => setOpenDialog(false)} sx={{ borderRadius: '10px', fontWeight: 600, color: '#718096' }}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              sx={{ bgcolor: '#263B7C', borderRadius: '10px', px: 4, py: 1.2, fontWeight: 700, '&:hover': { bgcolor: '#1e2e60' } }}
            >
              {formData.id ? 'Save Changes' : 'Create Subject'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default SubjectsPage;
